/*=============================================== TablePage ===============================================*/

import { useContext } from "react"
import { Link } from "react-router-dom"
import { Table, CodeContainer, Text, uuid } from "tsx-library-julseb"

import { LangContext } from "context"
import type { LangContextType } from "context"

import { ComponentsLayout } from "components"
import type { ItemType, PropsItemType } from "components"

type User = {
    id: number
    firstName: string
    lastName: string
    facebook: string
    twitter: string
}

const TablePage = () => {
    const { language } = useContext(LangContext) as LangContextType

    const headers: string[] = [
        "Id",
        "First Name",
        "Last Name",
        "Facebook",
        "Twitter",
    ]

    const users: User[] = [
        {
            id: 1,
            firstName: "Anna",
            lastName: "Fali",
            facebook: "Anna Fali",
            twitter: "@anna.fali",
        },
        {
            id: 2,
            firstName: "Niek",
            lastName: "Bove",
            facebook: "Niek Bove",
            twitter: "@niek.bove",
        },
        {
            id: 3,
            firstName: "Lacara",
            lastName: "Jones",
            facebook: "Lacara Jones",
            twitter: "@lacara.jones",
        },
        {
            id: 4,
            firstName: "Pablo",
            lastName: "Cambeiro",
            facebook: "Pablo Cambeiro",
            twitter: "@pablo.cambeiro",
        },
        {
            id: 5,
            firstName: "Irene",
            lastName: "Sotelo",
            facebook: "Irene Sotelo",
            twitter: "@irene.sotelo",
        },
    ]

    const content = () => (
        <>
            {users.map(user => (
                <tr key={uuid()}>
                    <td>{user.id}</td>
                    <td>{user.firstName}</td>
                    <td>{user.lastName}</td>
                    <td>
                        {/* eslint-disable-next-line */}
                        <a href="#">{user.facebook}</a>
                    </td>
                    <td>
                        {/* eslint-disable-next-line */}
                        <a href="#">{user.twitter}</a>
                    </td>
                </tr>
            ))}
        </>
    )

    const items: ItemType[] = [
        {
            title: "Bordered",
            content: <Table headers={headers}>{content()}</Table>,
            code: '<Table headers={headers}>\n    {users.map(user => (\n        <tr key={uuid()}>\n            <td>{user.id}</td>\n            <td>{user.firstName}</td>\n            <td>{user.lastName}</td>\n            <td>\n                <a href="#">{user.facebook}</a>\n            </td>\n            <td>\n                <a href="#">{user.twitter}</a>\n            </td>\n        </tr>\n    ))}\n</Table>',
        },
        {
            title: "Border bottom",
            content: (
                <Table variant="border-bottom">
                    <thead>
                        <tr>
                            {headers.map(header => (
                                <th key={uuid()}>{header}</th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>{content()}</tbody>
                </Table>
            ),
            code: '<Table variant="border-bottom">\n    <thead>\n        <tr>\n            {headers.map((header) => (\n                <th key={uuid()}>{header}</th>\n            ))}\n        </tr>\n    </thead>\n\n    <tbody>\n        {users.map((user) => (\n            <tr key={uuid()}>\n                <td>{user.id}</td>\n                <td>{user.firstName}</td>\n                <td>{user.lastName}</td>\n                <td>\n                    <a href="#">{user.facebook}</a>\n                </td>\n                <td>\n                    <a href="#">{user.twitter}</a>\n                </td>\n            </tr>\n        ))}\n    </tbody>\n</Table>',
        },
        {
            title: "Stripped",
            content: (
                <Table variant="stripped" headers={headers}>
                    {content()}
                </Table>
            ),
            code: '<Table variant="stripped" headers={headers}>\n    {users.map(user => (\n        <tr key={uuid()}>\n            <td>{user.id}</td>\n            <td>{user.firstName}</td>\n            <td>{user.lastName}</td>\n            <td>\n                <a href="#">{user.facebook}</a>\n            </td>\n            <td>\n                <a href="#">{user.twitter}</a>\n            </td>\n        </tr>\n    ))}\n</Table>',
        },
    ]

    const propsItems: PropsItemType[] = [
        {
            subtitle: "headers",
            type: "array of strings or array of JSX elements",
            default: (
                <>
                    When using this prop, you don't need to add a{" "}
                    <code>tbody</code>.
                </>
            ),
        },
        {
            subtitle: "variant",
            type: "string",
            possible: ["bordered", "stripped", "border-bottom"],
            default: "bordered",
        },
        {
            subtitle: "vAlign",
            type: "string",
            possible: [
                "baseline",
                "top",
                "middle",
                "bottom",
                "sub",
                "text-top",
            ],
        },
        {
            subtitle: "linkifyHeaders",
            intro: (
                <>
                    Adds the <Link to="/components/linkify">Linkify</Link>{" "}
                    component in the <code>headers</code>.
                </>
            ),
            type: "boolean",
            default: "true",
        },
    ]

    return (
        <ComponentsLayout items={items} propsItems={propsItems}>
            <Text>For all demos, we are using these arrays</Text>

            {language === "ts" ? (
                <CodeContainer language="typescript">
                    {
                        'type User = {\n    id: number\n    firstName: string\n    lastName: string\n    facebook: string\n    twitter: string\n}\n\nconst headers: string[] = [\n    "Id",\n    "First Name",\n    "Last Name",\n    "Facebook",\n    "Twitter",\n]\n\nconst users: User[] = [\n    {\n        id: 1,\n        firstName: "Anna",\n        lastName: "Fali",\n        facebook: "Anna Fali",\n        twitter: "@anna.fali",\n    },\n    {\n        id: 2,\n        firstName: "Niek",\n        lastName: "Bove",\n        facebook: "Niek Bove",\n        twitter: "@niek.bove",\n    },\n    {\n        id: 3,\n        firstName: "Lacara",\n        lastName: "Jones",\n        facebook: "Lacara Jones",\n        twitter: "@lacara.jones",\n    },\n    {\n        id: 4,\n        firstName: "Pablo",\n        lastName: "Cambeiro",\n        facebook: "Pablo Cambeiro",\n        twitter: "@pablo.cambeiro",\n    },\n    {\n        id: 5,\n        firstName: "Irene",\n        lastName: "Sotelo",\n        facebook: "Irene Sotelo",\n        twitter: "@irene.sotelo",\n    },\n]'
                    }
                </CodeContainer>
            ) : (
                <CodeContainer language="javascript">
                    {
                        'const headers = [\n    "Id",\n    "First Name",\n    "Last Name",\n    "Facebook",\n    "Twitter",\n]\n\nconst users = [\n    {\n        id: 1,\n        firstName: "Anna",\n        lastName: "Fali",\n        facebook: "Anna Fali",\n        twitter: "@anna.fali",\n    },\n    {\n        id: 2,\n        firstName: "Niek",\n        lastName: "Bove",\n        facebook: "Niek Bove",\n        twitter: "@niek.bove",\n    },\n    {\n        id: 3,\n        firstName: "Lacara",\n        lastName: "Jones",\n        facebook: "Lacara Jones",\n        twitter: "@lacara.jones",\n    },\n    {\n        id: 4,\n        firstName: "Pablo",\n        lastName: "Cambeiro",\n        facebook: "Pablo Cambeiro",\n        twitter: "@pablo.cambeiro",\n    },\n    {\n        id: 5,\n        firstName: "Irene",\n        lastName: "Sotelo",\n        facebook: "Irene Sotelo",\n        twitter: "@irene.sotelo",\n    },\n]'
                    }
                </CodeContainer>
            )}
        </ComponentsLayout>
    )
}

export default TablePage
